import { LegalPageLayout } from '@/components/layout/LegalPageLayout';
import { LEGAL_SUITE_VERSION, LEGAL_SUITE_LAST_UPDATED_LABEL } from '@/constants/legalSuite';

export default function PrivacyPolicy() {
  return (
    <LegalPageLayout
      title="Política de Privacidad"
      lastUpdated={LEGAL_SUITE_LAST_UPDATED_LABEL}
      version={LEGAL_SUITE_VERSION}
    >
      {/* Tabla de contenido */}
      <nav className="bg-muted/50 rounded-lg p-4 sm:p-6 mb-8 border">
        <p className="font-semibold text-foreground mb-3">Contenido</p>
        <ol className="!text-sm space-y-1">
          <li><a href="#datos-recolectados">Qué datos recolectamos</a></li>
          <li><a href="#uso-datos">Para qué los usamos</a></li>
          <li><a href="#legitimacion">Base de legitimación y consentimiento</a></li>
          <li><a href="#almacenamiento">Cómo se almacenan</a></li>
          <li><a href="#suscripcion">Suscripción y pagos (Stripe)</a></li>
          <li><a href="#compartir">Con quién se comparten</a></li>
          <li><a href="#ia-openai">Inteligencia artificial y OpenAI</a></li>
          <li><a href="#sesion-navegador">Sesión en el navegador</a></li>
          <li><a href="#retencion">Retención de datos</a></li>
          <li><a href="#derechos">Derechos del usuario</a></li>
          <li><a href="#contacto">Contacto</a></li>
          <li><a href="#cambios">Cambios a esta política</a></li>
        </ol>
      </nav>

      <p>
        En <strong>FOCO (Tu Dinero Tu Poder)</strong> nos comprometemos a proteger la privacidad y los datos
        personales de nuestros usuarios. Esta Política de Privacidad describe cómo recolectamos, usamos,
        almacenamos y protegemos tu información cuando utilizas nuestra plataforma. Está dirigida a usuarios en{' '}
        <strong>México, Centroamérica y Costa Rica</strong>.
      </p>

      <p className="mt-4 text-sm text-muted-foreground">
        Esta página es un resumen ejecutivo de la política oficial descrita en{' '}
        <strong>docs/politicas/politica-privacidad.md</strong> (versión {LEGAL_SUITE_VERSION}). En caso de discrepancia, prevalecerá
        el texto oficial.
      </p>

      <h2 id="datos-recolectados">1. Qué datos recolectamos</h2>
      <p>Recopilamos únicamente los datos necesarios para ofrecerte el servicio:</p>
      <ul>
        <li>
          <strong>Datos de cuenta y perfil:</strong> correo electrónico, nombre, contraseña (almacenada de forma
          segura y no legible) y, opcionalmente, foto de perfil (avatar). Si inicias sesión con Google, recibimos el
          correo y el nombre asociados a esa cuenta.
        </li>
        <li>
          <strong>Datos financieros que tú introduces:</strong> transacciones (montos, fechas, categorías, tipos),
          metas financieras, deudas (nombres, saldos, intereses, pagos), presupuestos por categoría.{' '}
          <strong>No almacenamos</strong> en nuestros servidores el número completo de tu tarjeta ni credenciales de
          acceso a bancos de terceros; solo lo que registras en la app o importas desde un PDF que tú subes. Estos
          datos pueden considerarse <strong>sensibles</strong> en algunas jurisdicciones por su naturaleza financiera.
        </li>
        <li>
          <strong>Datos de suscripción y facturación:</strong> plan (gratuito o Premium), estado de la suscripción,
          tipo de plan (mensual o anual), fecha de renovación, identificadores en <strong>Stripe</strong> y contador de
          importaciones exitosas de PDF (límites del plan gratuito). Los datos de pago los introduces en las páginas
          seguras de Stripe; FOCO no guarda el número completo de la tarjeta.
        </li>
        <li>
          <strong>Uso del chat con asesoría por IA:</strong> los mensajes que envías en el chat y las respuestas
          generadas se almacenan para darte continuidad en la conversación y mejorar el servicio.
        </li>
        <li>
          <strong>Datos técnicos y de uso:</strong> dirección IP, tipo de navegador y dispositivo cuando accedes a la
          app; registros de inicio de sesión y de actividad necesarios para la seguridad y el correcto funcionamiento
          del servicio.
        </li>
        <li>
          <strong>Archivos cargados:</strong> estados de cuenta bancarios en <strong>PDF</strong> que subas
          voluntariamente. Si usas la importación asistida por IA, el contenido del PDF se envía a{' '}
          <strong>OpenAI</strong> para sugerirte movimientos; solo cuando tú inicias la acción con sesión
          iniciada.
        </li>
        <li>
          <strong>Cookies y tecnologías similares:</strong> según lo descrito en nuestra Política de Cookies.
        </li>
      </ul>

      <h2 id="uso-datos">2. Para qué los usamos</h2>
      <p>Utilizamos tus datos personales para:</p>
      <ul>
        <li>
          <strong>Prestar el servicio:</strong> permitirte registrar y ver tus transacciones, metas, deudas, reportes
          y puntuación FOCO; ofrecerte el chat con asesoría por IA y las alertas o recordatorios que correspondan a tu
          plan (algunas requieren <strong>Premium</strong> o rol de administrador).
        </li>
        <li>
          <strong>Gestionar tu cuenta:</strong> autenticación, recuperación de contraseña, verificación de correo y
          gestión de tu perfil (nombre, moneda, preferencias).
        </li>
        <li>
          <strong>Gestionar suscripciones y pagos:</strong> altas, renovaciones, cancelaciones e impagos; aplicar
          límites del plan gratuito; enviarte avisos de pago fallido u otros relacionados con tu suscripción.
        </li>
        <li>
          <strong>Cumplimiento y seguridad:</strong> cumplir con obligaciones legales, proteger la seguridad del
          servicio y de los usuarios (por ejemplo, detectar usos indebidos o responder a solicitudes legítimas de
          autoridades).
        </li>
        <li>
          <strong>Relación con tu asesor:</strong> si eres cliente de un asesor que usa FOCO, ese asesor puede ver,
          dentro del panel de la aplicación, información agregada o de seguimiento sobre tu uso (estado de avance,
          alertas o tareas), según las funcionalidades que ofrece la app.
        </li>
        <li>
          <strong>Mejorar la experiencia y la plataforma:</strong> analizar el uso de forma agregada o anonimizada
          para mejorar funcionalidades, usabilidad y seguridad.
        </li>
      </ul>

      <h2 id="legitimacion">3. Base de legitimación y consentimiento</h2>
      <p>
        El tratamiento de tus datos se fundamenta en tu <strong>consentimiento libre, expreso, específico e informado</strong>,
        otorgado al registrarte y aceptar los Términos y Condiciones, la Política de Privacidad, la Política de Cookies,
        la información sobre consentimiento y el consentimiento unificado (versión {LEGAL_SUITE_VERSION}). Para datos
        financieros que puedan considerarse sensibles, solicitamos consentimiento
        explícito adicional, especialmente para el uso de funcionalidades de inteligencia artificial.
      </p>
      <p>
        Puedes revocar tu consentimiento en cualquier momento desde la sección de Configuración de tu cuenta o
        contactando al responsable indicado, sin que ello afecte la licitud del tratamiento previo a la revocación.
      </p>

      <h2 id="almacenamiento">4. Cómo se almacenan</h2>
      <p>Sus datos se almacenan en servidores seguros proporcionados por nuestra infraestructura en la nube. Implementamos:</p>
      <ul>
        <li>Cifrado en tránsito (HTTPS/TLS) y en reposo.</li>
        <li>Políticas de seguridad a nivel de fila (Row Level Security) para garantizar que solo usted pueda acceder a sus datos.</li>
        <li>Contraseñas cifradas con algoritmos de hash seguros.</li>
        <li>Acceso restringido mediante autenticación y roles de usuario.</li>
      </ul>

      <h2 id="suscripcion">5. Suscripción y pagos (Stripe)</h2>
      <p>
        Si contratas <strong>Premium</strong>, te redirigimos a <strong>Stripe Checkout</strong> o al{' '}
        <strong>Portal de cliente de Stripe</strong> para pagar o gestionar tu suscripción. Stripe trata los datos de
        pago que introduces en sus páginas. FOCO recibe de Stripe, mediante webhooks seguros, el estado de tu
        suscripción y los identificadores necesarios para activar funciones Premium. Stripe puede operar en{' '}
        <strong>EE. UU.</strong> y otros países según sus{' '}
        <a
          href="https://stripe.com/legal"
          className="text-primary underline hover:text-primary/80"
          target="_blank"
          rel="noopener noreferrer"
        >
          términos
        </a>{' '}
        y{' '}
        <a
          href="https://stripe.com/privacy"
          className="text-primary underline hover:text-primary/80"
          target="_blank"
          rel="noopener noreferrer"
        >
          política de privacidad
        </a>
        .
      </p>

      <h2 id="compartir">6. Con quién se comparten</h2>
      <p>
        <strong>No vendemos, alquilamos ni compartimos tus datos personales con terceros</strong> para fines de
        marketing. Tus datos pueden ser compartidos únicamente en los siguientes casos:
      </p>
      <ul>
        <li>
          <strong>Tu asesor financiero:</strong> si usas FOCO a través de un asesor, este puede ver información
          agregada o de seguimiento sobre tu avance dentro del panel de la app.
        </li>
        <li>
          <strong>Proveedores de infraestructura:</strong> servicios de alojamiento, bases de datos, almacenamiento y
          funciones en la nube necesarios para operar la plataforma.
        </li>
        <li>
          <strong>Stripe (pagos):</strong> procesamiento de suscripciones y cobros; actúa como encargado del tratamiento
          de datos de pago según sus políticas.
        </li>
        <li>
          <strong>Servicios de correo electrónico:</strong> para enviar notificaciones y comunicaciones relacionadas
          con tu cuenta.
        </li>
        <li>
          <strong>Servicios de inteligencia artificial (OpenAI):</strong> el chat con asesoría, los tips
          personalizados y la extracción desde PDF invocan modelos de <strong>OpenAI</strong> desde nuestros servidores
          (Supabase Edge Functions), no mediante una pasarela separada tipo “Lovable AI Gateway” para esas funciones.
          Se envían solo los datos necesarios: mensajes de chat y resumen de contexto financiero; y el{' '}
          <strong>PDF completo</strong> si tú solicitas la importación asistida. Detalle técnico y checklist interno:{' '}
          <code className="text-xs">docs/security/openai-data-and-controls.md</code> en el repositorio del producto.
        </li>
        <li>
          <strong>Requerimientos legales:</strong> cuando sea exigido por ley o autoridad competente, en los términos
          que marque la normativa aplicable.
        </li>
      </ul>

      <p>
        Estos proveedores pueden tratar datos en servidores ubicados fuera de tu país de residencia. Las condiciones
        exactas (incluido uso para mejora de productos o entrenamiento) dependen del <strong>plan contratado con OpenAI</strong>{' '}
        y de sus términos vigentes; cuando la consola o el contrato lo permitan, aplicamos opciones de privacidad para
        limitar el uso secundario de tu información financiera (p. ej. <code className="text-xs">store: false</code>{' '}
        en cada llamada para no retener prompts del lado del proveedor).
      </p>

      <h2 id="ia-openai">6 bis. Inteligencia artificial y OpenAI</h2>
      <p>
        Las respuestas del asistente y las sugerencias de importación son <strong>orientativas</strong>; no sustituyen
        asesoría legal, tributaria ni financiera profesional.
      </p>
      <ul>
        <li>
          <strong>Chat y tips:</strong> mensajes que escribes y un resumen estructurado de tu situación (FOCO Score,
          ingresos, gastos, metas, deudas, tendencias, etc.) se envían a la API de OpenAI tras tu inicio de sesión y,
          donde la app lo exige, tras tu consentimiento específico para IA.
        </li>
        <li>
          <strong>Importación PDF:</strong> es voluntaria. Si no quieres enviar el PDF a un tercero, puedes registrar
          transacciones manualmente sin usar la función con IA.
        </li>
        <li>
          <strong>Opt-out:</strong> puedes negarte al uso de IA en el flujo de consentimiento del asistente o revocar
          preferencias desde Configuración; eso puede desactivar el chat con IA sin borrar necesariamente el resto de
          tu cuenta (te lo indicaremos en pantalla).
        </li>
        <li>
          Enlaces útiles del proveedor (versión vigente):{' '}
          <a
            href="https://openai.com/policies/api-data-usage-policies/"
            className="text-primary underline hover:text-primary/80"
            target="_blank"
            rel="noopener noreferrer"
          >
            Políticas de uso de datos de la API de OpenAI
          </a>
          {' · '}
          <a
            href="https://openai.com/policies/privacy-policy/"
            className="text-primary underline hover:text-primary/80"
            target="_blank"
            rel="noopener noreferrer"
          >
            Política de privacidad de OpenAI
          </a>
          .
        </li>
      </ul>

      <h2 id="sesion-navegador">6 ter. Sesión en el navegador (cookies y tokens)</h2>
      <p>
        La aplicación web puede persistir tu sesión guardando tokens de autenticación en el{' '}
        <strong>almacenamiento local del navegador</strong> (<code className="text-xs">localStorage</code>), no solo
        mediante cookies con <strong>HttpOnly</strong>. Es habitual en este tipo de app con el proveedor de
        autenticación que usa FOCO. Las buenas prácticas sobre cookies <strong>Secure</strong>,{' '}
        <strong>HttpOnly</strong> y <strong>SameSite</strong> aplican de forma directa a las{' '}
        <strong>cookies</strong> que el sitio o terceros establezcan; la protección frente al acceso indebido al
        token desde scripts maliciosos incluye <strong>HTTPS</strong>, cabeceras de seguridad y mitigación de XSS,
        como se describe en la <strong>Política de cookies</strong> y en la documentación técnica interna del
        producto.
      </p>

      <h2 id="retencion">7. Retención de datos</h2>
      <p>
        Conservamos tus datos personales mientras mantengas una cuenta activa y, tras la baja, el tiempo necesario
        para cumplir obligaciones legales (por ejemplo, tributarias o contables) o para resolver reclamaciones. Los
        registros de acceso (logs) se conservarán, de forma general, durante un plazo máximo de 90 días, salvo que
        debamos conservarlos por más tiempo para atender una investigación de seguridad o una obligación legal.
      </p>

      <h2 id="derechos">8. Derechos del usuario</h2>
      <p>
        Puedes ejercer distintos derechos según la ley que te aplique (México, Costa Rica u otros países de
        Centroamérica). De forma general, tienes derecho a:
      </p>
      <ul>
        <li>
          <strong>Acceso:</strong> solicitar una copia de los datos personales que tenemos sobre ti y conocer para qué
          se utilizan.
        </li>
        <li>
          <strong>Rectificación:</strong> corregir datos inexactos o incompletos.
        </li>
        <li>
          <strong>Supresión / Cancelación:</strong> solicitar la eliminación de tus datos personales cuando la ley lo
          permita.
        </li>
        <li>
          <strong>Oposición y limitación:</strong> oponerte al tratamiento para ciertas finalidades o solicitar que se
          limite.
        </li>
        <li>
          <strong>Portabilidad:</strong> recibir tus datos en un formato estructurado y legible cuando la ley lo
          prevea.
        </li>
        <li>
          <strong>Revocación del consentimiento:</strong> retirar tu consentimiento en cualquier momento.
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        Para detalles específicos por país (México – derechos ARCO y plazos; Costa Rica – Ley 8968 y PRODHAB; otros
        países de Centroamérica), consulta la versión completa en{' '}
        <strong>docs/politicas/politica-privacidad.md</strong> (versión {LEGAL_SUITE_VERSION}).
      </p>

      <h2 id="contacto">9. Contacto y responsable</h2>
      <p>
        <strong>Responsable del tratamiento:</strong> Erick S Meono, Managing Member (miembro gestor) — producto FOCO —
        Tu Dinero Tu Poder.
      </p>
      <p>
        <strong>Domicilio del responsable:</strong> 9850 S Maryland Pkwy Ste A-5557, Las Vegas, Nevada 89183, Estados
        Unidos de América.
      </p>
      <p>
        El responsable del tratamiento opera desde <strong>Estados Unidos (Nevada)</strong>. Esto es relevante para el
        reconocimiento de <strong>transferencias internacionales</strong> de datos cuando el usuario se encuentre fuera
        de EE. UU.
      </p>
      <p>Para ejercer tus derechos o realizar consultas sobre esta política, puedes contactarnos a través de:</p>
      <ul>
        <li>
          <strong>Correo electrónico:</strong> info@premium-advisor.com
        </li>
        <li>
          <strong>Sitio web:</strong> www.tudinerotupoder.com
        </li>
      </ul>

      <h2 id="cambios">10. Cambios a esta política</h2>
      <p>
        Nos reservamos el derecho de actualizar esta Política de Privacidad en cualquier momento. Los cambios
        significativos serán notificados a través de la plataforma o por correo electrónico cuando sea posible. La
        fecha de la última actualización se indica al inicio de este documento. El uso continuado de la plataforma
        después de cualquier modificación constituye tu aceptación de la política actualizada.
      </p>
    </LegalPageLayout>
  );
}
